import {
  AloftArea,
  AloftPrice,
  expected,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getDepreciatedCostPageRoute } from '../utils/getDepreciatedCostPageRoute.ts';

type SiteProperty = {
  id: string;
  name: string;
  size: AloftArea;
  value: AloftPrice;
};

type InconsistencyScenario = 'smallest' | 'largest';

const getSiteArea = (
  propertyID: string,
  propertyDataEntities: Parameters<AlertDefinition>[0]['propertyDataEntities'],
) => {
  if (propertyID === 'subject') {
    return orUndefined(schAloftArea.parse)(
      (propertyDataEntities.subject as SubjectPropertyDataState | undefined)?.subjectData
        .editableData.site?.area,
    );
  }

  return orUndefined(schAloftArea.parse)(
    (propertyDataEntities[propertyID] as CompPropertyDataState | undefined)?.compData.editableData
      .site?.area,
  );
};

const getPropertyName = (propertyID: string, compSequence: string[]) =>
  propertyID === 'subject' ? 'Subject' : `Comparable ${compSequence.indexOf(propertyID) + 1}`;

const getPropertiesToCompare = (
  compSequence: string[],
  propertyDataEntities: Parameters<AlertDefinition>[0]['propertyDataEntities'],
  siteValues: Parameters<AlertDefinition>[0]['siteValues'],
): SiteProperty[] =>
  ['subject', ...compSequence]
    .map((propertyID) => ({
      id: propertyID,
      name: getPropertyName(propertyID, compSequence),
      size: getSiteArea(propertyID, propertyDataEntities),
      value: siteValues[propertyID],
    }))
    .filter(
      (property): property is SiteProperty => isDefined(property.size) && isDefined(property.value),
    );

const getInconsistencies = (properties: SiteProperty[]) => {
  const minSiteValue = expected(AloftPrice.min(properties.map((property) => property.value)));
  const maxSiteValue = expected(AloftPrice.max(properties.map((property) => property.value)));
  const minSiteArea = expected(AloftArea.min(properties.map((property) => property.size)));
  const maxSiteArea = expected(AloftArea.max(properties.map((property) => property.size)));

  const inconsistencies: Array<{
    scenario: InconsistencyScenario;
    sourceProperty: SiteProperty;
    comparisonProperty: SiteProperty;
  }> = [];

  const smallestSites = properties.filter((property) => property.size.equals(minSiteArea));
  const lowestValueSites = properties.filter((property) => property.value.equals(minSiteValue));
  const smallestSiteWithoutLowestValue =
    smallestSites.length === 1 && lowestValueSites.length === 1
      ? smallestSites.find((property) => property.value.gt(minSiteValue.mul(1.05)))
      : undefined;

  if (smallestSiteWithoutLowestValue) {
    inconsistencies.push({
      scenario: 'smallest',
      sourceProperty: smallestSiteWithoutLowestValue,
      comparisonProperty: lowestValueSites[0]!,
    });
  }

  const largestSites = properties.filter((property) => property.size.equals(maxSiteArea));
  const highestValueSites = properties.filter((property) => property.value.equals(maxSiteValue));
  const largestSiteWithoutHighestValue =
    largestSites.length === 1 && highestValueSites.length === 1
      ? largestSites.find((property) => property.value.lt(maxSiteValue.mul(0.95)))
      : undefined;

  if (largestSiteWithoutHighestValue) {
    inconsistencies.push({
      scenario: 'largest',
      sourceProperty: largestSiteWithoutHighestValue,
      comparisonProperty: highestValueSites[0]!,
    });
  }

  return inconsistencies;
};

const getMessage = (
  scenario: InconsistencyScenario,
  sourceProperty: SiteProperty,
  comparisonProperty: SiteProperty,
) =>
  scenario === 'smallest'
    ? `Smallest Site (${sourceProperty.name}: ${sourceProperty.size.toString()} | ${sourceProperty.value.toString()}) does not have the lowest Site Value (${comparisonProperty.name}: ${comparisonProperty.size.toString()} | ${comparisonProperty.value.toString()})`
    : `Largest Site (${sourceProperty.name}: ${sourceProperty.size.toString()} | ${sourceProperty.value.toString()}) does not have the highest Site Value (${comparisonProperty.name}: ${comparisonProperty.size.toString()} | ${comparisonProperty.value.toString()})`;

const getTips = (scenario: InconsistencyScenario) =>
  scenario === 'smallest'
    ? 'Smaller sites typically have lower site values. If this relationship is inverted, justify the current values based on factors like location, zoning, or utility.'
    : 'Larger sites typically have higher site values. If this relationship is inverted, justify the current values based on factors like location, zoning, or utility.';

export const inconsistentSiteSizeToSiteValue: AlertDefinition = ({
  compSequence,
  order,
  propertyDataEntities,
  siteValues,
}) => {
  const properties = getPropertiesToCompare(compSequence, propertyDataEntities, siteValues);

  if (properties.length < 2) {
    return [];
  }

  return getInconsistencies(properties).map((inconsistency) => ({
    type: schAlertType.enum.inconsistentSiteSizeToSiteValue,
    status: 'active' as const,
    severity: 'error' as const,
    orderType: ['saas', 'appraisify'] as const,
    message: getMessage(
      inconsistency.scenario,
      inconsistency.sourceProperty,
      inconsistency.comparisonProperty,
    ),
    propertyID: inconsistency.sourceProperty.id,
    fixingInfo: {
      tips: getTips(inconsistency.scenario),
      pairedCostRouteItem: getDepreciatedCostPageRoute(order),
      fixingMethod: ['comment', 'action'] as const,
      commentAlwaysRequired: false,
    },
    triggers: [] as const,
  }));
};
